<script setup>
  import { onMounted, ref, watch } from "vue";
  import { useFetch } from "@/composables/useFetch.js";
  import { uri } from "@/constants/config.js";
  import "@bhplugin/vue3-datatable/dist/style.css";
  import { useRouter } from "vue-router";
  import BasePagination from "@/base-components/Table/BasePagination.vue";
  import Lucide from "@/base-components/Lucide";
  import InstanceTables from "@/components/Tables/InstanceTables.vue";
  import { useUserStore } from "@/stores/user.js";
  import Multiselect from "vue-multiselect";

  const userStore = useUserStore();
  const router = useRouter();
  const vmList = ref([]);

  const { role } = defineProps({
    role: {
      type: Boolean,
      default: false,
    },
  });

  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });
  const data = null;
  const loadingFlag = ref(false);

  async function getVMList() {
    loadingFlag.value = true;

    const params = new URLSearchParams();
    params.set("page", pageOptions.value.page);
    if (userSelected.value) {
      params.set("ownerId", userSelected.value.id);
    }
    if (cloudSelected.value) {
      params.set("domainId", cloudSelected.value.id);
    }

    const urls = {
      cloud: uri.admin.cloud.iaas.instance.list,
      admin: uri.admin.iaas.instance.list,
    };
    const url = urls[role];

    try {
      // Await the result of useFetch

      const { data, error } = await useFetch(url, {
        method: "GET",
        params,
      });
      if (data) {
        vmList.value = data.data;
        pageOptions.value.count = data.meta.total;
        pageOptions.value.limit = data.meta.perPage;
      }
    } catch (err) {
      console.log(err);
    } finally {
      loadingFlag.value = false;
    }
  }

  function rowClick(instance) {
    const routes = {
      cloud: `/cloud/vm/${instance.id}`,
      admin: `/manager/vm/${instance.id}`,
    };
    const route = routes[role];
    router.push(`/cloud/vm/${instance.id}`);
  }

  const userSelected = ref(null);
  const isLoadingUsers = ref(false);
  const usersList = ref([]);

  function selectUser(data) {
    selectUser.value = data;
    getVMList();
  }
  function selectDomain(data) {
    cloudSelected.value = data;
    getVMList();
  }

  async function findUser(query) {
    if (!query.trim().length) return;
    const temps = { cloud: "cloud", admin: "manager" };
    const temp = temps[role];

    isLoadingUsers.value = true;
    usersList.value = await userStore.findUsers({ q: query }, temp);
    isLoadingUsers.value = false;
  }

  const cloudList = ref([]);
  const loadingFlagCloudDomainList = ref(false);
  const cloudSelected = ref(null);
  async function getCloudDomainList() {
    loadingFlagCloudDomainList.value = true;
    try {
      // Await the result of useFetch

      const { data, error } = await useFetch(uri.admin.cloud.list, {
        method: "GET",
      });
      if (data) {
        cloudList.value = data;
      }
    } catch (err) {
      console.log(err);
    } finally {
      loadingFlagCloudDomainList.value = false;
    }
  }

  getVMList();
  if (role === "cloud") getCloudDomainList();
</script>

<template>
  <section>
    <div class="grid grid-cols-12 gap-6 mt-8">
      <div class="col-span-12 lg:col-span-12 2xl:col-span-12">
        <!-- BEGIN: Inbox Filter -->
        <div class="flex flex-col-reverse items-center intro-y sm:flex-row">
          <div class="relative w-full mt-3 mr-auto sm:w-auto sm:mt-0"></div>
          <div class="flex w-full sm:w-auto gap-2"></div>
        </div>
        <!-- END: Inbox Filter -->
        <!-- BEGIN: Inbox Content -->
        <div class="mt-5 intro-y box">
          <div
            class="w-full flex flex-col items-center gap-2 sm:gap-0 sm:flex-row sm:justify-between sm:items-center px-5 py-0 border-b text-slate-500 border-slate-200/60"
          >
            <!-- BEGIN: domain search field -->
            <div
              v-if="role === 'cloud'"
              class="w-full sm:w-2/5 sm:mt-5 sm:mb-5"
            >
              <div class="mb-1">{{ $t("label.searchDomain") }}</div>
              <Multiselect
                class="primary mb-1.5"
                v-model="cloudSelected"
                name="id"
                deselect-label=""
                :is-multiple="false"
                track-by="id"
                :options="cloudList"
                :searchable="true"
                :allow-empty="true"
                :is-preselect-first="false"
                :loading="loadingFlagCloudDomainList"
                @remove="selectDomain(null)"
                @select="selectDomain"
                :internal-search="false"
                :customLabel="
                  (data) => {
                    return data.displayName;
                  }
                "
              >
              </Multiselect>
            </div>
            <!-- END: domain search field -->
            <!-- BEGIN: user search field -->
            <div class="w-full sm:w-2/5 sm:mt-5 sm:mb-5">
              <div class="mb-1">{{ $t("label.searchUser") }}</div>
              <Multiselect
                class="primary mb-1.5"
                v-model="userSelected"
                name="id"
                deselect-label=""
                :is-multiple="false"
                track-by="id"
                :options="usersList"
                :searchable="true"
                :allow-empty="true"
                :is-preselect-first="false"
                :loading="isLoadingUsers"
                :customLabel="
                  (data) => {
                    const contanctData = data.mobile || data.email;
                    let res =
                      data.Profile.firstName + ' ' + data.Profile.lastName;
                    if (contanctData && contanctData.length > 0) {
                      res += ' (' + contanctData + ')';
                    }

                    return res;
                  }
                "
                @remove="selectUser(null)"
                @select="selectUser"
                @search-change="findUser"
                :internal-search="false"
              >
              </Multiselect>
            </div>
            <!-- END: user search field -->

            <div
              class="mb-4 sm:m-0 w-5 h-5 ms-5 cursor-pointer"
              @click="getVMList"
            >
              <Lucide icon="RefreshCw" class="w-6 h-6" />
            </div>
          </div>
          <div class="mb-5 relative"></div>
          <InstanceTables
            admin-mode="cloud"
            :rows="vmList"
            :loading="loadingFlag"
            :page-options="pageOptions"
            @row-click="rowClick"
          />
          <div
            class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
          >
            <div>
              <BasePagination
                v-if="pageOptions.count > pageOptions.limit"
                :page-options="pageOptions"
                @page-change="getVMList"
              />
            </div>
            <div class="mt-2 sm:ml-auto sm:mt-0"></div>
          </div>
        </div>
        <!-- END: Inbox Content -->
      </div>
    </div>
  </section>
</template>

<style scoped></style>
